import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ConsentESign } from "@/components/ConsentESign";
import { CheckCircle2, HelpCircle, LogOut } from "lucide-react";

const errMsg = (e) => e?.response?.data?.detail || "Something went wrong";

export const ConsentActions = ({ onDone }) => {
  const { logout } = useAuth();
  const [mode, setMode] = useState("continue");
  const [note, setNote] = useState("");
  const [confirmExit, setConfirmExit] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async (decision) => {
    setBusy(true);
    try {
      await api.post("/suspension/respond", { decision, note, confirm_exit: decision === "exit" });
      if (decision === "exit") {
        toast.success("Your exit decision has been recorded. You will now be signed out.");
        setTimeout(logout, 1800);
        return;
      }
      toast.success("Your clarification request has been sent to the Founder.");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const tab = (id, label, Icon) => (
    <button key={id} type="button" onClick={() => { setMode(id); setConfirmExit(false); }} data-testid={`consent-mode-${id}`}
      className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-semibold transition-colors ${mode === id ? "bg-navy text-gold" : "bg-slate-100 text-navy hover:bg-slate-200"}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  return (
    <div className="space-y-4" data-testid="consent-actions">
      <div className="flex gap-2">
        {tab("continue", "I Will Continue", CheckCircle2)}
        {tab("clarify", "Need Clarification", HelpCircle)}
        {tab("exit", "I Wish to Exit", LogOut)}
      </div>
      {mode === "continue" && <ConsentESign onDone={onDone} />}
      {mode === "clarify" && (
        <div className="space-y-3">
          <Textarea placeholder="What would you like clarified before you decide?" value={note} onChange={(e) => setNote(e.target.value)} data-testid="consent-clarify-input" />
          <Button disabled={busy || note.trim().length < 5} onClick={() => send("clarify")} className="w-full bg-navy text-gold hover:bg-navy/90" data-testid="consent-submit-clarify">
            Send to Founder
          </Button>
        </div>
      )}
      {mode === "exit" && (
        <div className="space-y-3">
          <Textarea placeholder="Reason for leaving (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="consent-exit-reason" />
          {!confirmExit ? (
            <Button variant="destructive" disabled={busy} onClick={() => setConfirmExit(true)} className="w-full" data-testid="consent-submit-exit">
              I Wish to Exit the Company
            </Button>
          ) : (
            <div role="alertdialog" className="rounded-lg border-2 border-red-300 bg-red-50 p-4" data-testid="consent-exit-confirm-dialog">
              <div className="font-heading text-sm font-bold text-red-800">Final confirmation: exit the company?</div>
              <p className="mt-1 text-sm text-red-900">
                Your access to VNS Vinayaka Desk and all team groups will be closed immediately and the Founder will be informed.
                Your records are kept. Only the Founder can reinstate your access.
              </p>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" onClick={() => setConfirmExit(false)} className="flex-1" data-testid="consent-exit-cancel">Go back</Button>
                <Button disabled={busy} onClick={() => send("exit")} className="flex-1 bg-red-600 text-white hover:bg-red-700" data-testid="consent-exit-confirm">Yes, I want to exit</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
